import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import moment from "moment";
import { LiaInfoSolid } from "react-icons/lia";
import { checkAcessRights } from "../../../_helper/helper";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../../_contstants/common";
import {
  closeDialog,
  openDialog,
} from "../../../components/dialog/dialogSlice";
import { clearLeadID, setLeadID } from "../../lead/leadSlice";
import ButtonEdit from "../../../components/drawer/ButtonEdit";
import IconButton from "../../../components/common/IconButton";
import { MdDelete, MdKeyboardBackspace } from "react-icons/md";
import { LoadingPanel } from "../../../components/layout/Loading";
import LinkEdit from "../../../components/drawer/LinkEdit";
import { deleteLead } from "../../lead/services/lead.services";
import AppDialog, {
  DialogCloseButton,
} from "../../../components/dialog/Dialog";
import { getDashboardLeads } from "../services/dashboard.services";
import AlertBox from "../../../components/common/AlertBox";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

const DashboardLeads: React.FC = () => {
  const navigate = useNavigate();
  return (
    <>
      <DeleteLeadDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton
              fillMode="solid"
              title="Back"
              onClick={() => navigate(-1)}
              icon={MdKeyboardBackspace}
              iconClassName="absolute-position"
              style={{ height: 30 }}
              iconStyle={{ fontSize: "20px" }}
            />
            <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
              Lead List
            </Typography.h4>
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <LeadGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const LeadGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isAddAccess = checkAcessRights("/inquiry", INSERT_ACCESS);
  const isEditAccess = checkAcessRights("/lead", EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights("/lead", DELETE_ACCESS);

  const LeadList = useAppSelector((state) => state.dashboard.DashboardLeadList);
  const loading = useAppSelector((state) => state.dashboard.loading);
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);
  const payload = {
    FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
    ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
    SectorID: dateFilter?.SectorID ? dateFilter?.SectorID : null,
  };

  useEffect(() => {
    dispatch(getDashboardLeads(payload));
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? LeadList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleAddInquiry = (LeadID: number) => {
      navigate("/inquiry/create", {
        state: {
          LeadID: LeadID,
        },
      });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteLead"));
      dispatch(setLeadID(ID));
    };
    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.style, right: 0 }}
      >
        {isEditAccess && (
          <ButtonEdit ID={props.dataItem.ID} drawerName="leadForm" />
        )}
        {isAddAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Create Inquiry"
            onClick={() => handleAddInquiry(props.dataItem.ID)}
            icon={LiaInfoSolid}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 5 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const gridRef = useRef<any>(null);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {LeadList && LeadList?.length > 0 ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 150px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(LeadList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : LeadList.slice(page.skip, page.take + page.skip)
          }
          skip={page.skip}
          take={page.take}
          total={LeadList.length}
          onFilterChange={handleFilterChange}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Name"
            title="Name"
            width={300}
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <LinkEdit ID={props.dataItem.ID} drawerName="leadForm">
                    <>
                      <div className="hoverableName">
                        {props.dataItem?.FirstName} {props.dataItem?.LastName}
                      </div>
                      <p className="m-0" style={{ fontWeight: 400 }}>
                        {" "}
                        {props.dataItem?.MobileNo}
                      </p>
                      <p className="m-0" style={{ fontWeight: 400 }}>
                        {" "}
                        {props.dataItem?.EmailID}
                      </p>
                    </>
                  </LinkEdit>
                ) : (
                  <div className="normalName">
                    <p className="m-0">
                      {props.dataItem?.FirstName} {props.dataItem?.LastName}
                    </p>
                    <p className="m-0" style={{ fontWeight: 400 }}>
                      {" "}
                      {props.dataItem?.MobileNo}
                    </p>
                    <p className="m-0" style={{ fontWeight: 400 }}>
                      {" "}
                      {props.dataItem?.EmailID}
                    </p>
                  </div>
                )}
              </td>
            )}
          />
          <Column
            field="LeadDate"
            title="Lead Date"
            width={200}
            cell={(props: any) => {
              const toDay = moment();
              const LeadDate = moment(
                String(props.dataItem?.LeadDate),
                "DD/MM/YYYY",
                true,
              );
              let remainingDuration = "";

              if (toDay.isValid() && LeadDate.isValid()) {
                const duration = moment?.duration(toDay?.diff(LeadDate));
                const durationChunks = {
                  years: duration.years(),
                  months: duration.months(),
                  days: duration.days(),
                };

                remainingDuration = `${
                  durationChunks.years > 0
                    ? `${durationChunks.years} Years and `
                    : ""
                } ${
                  durationChunks.months > 0
                    ? `${durationChunks.months} Months and `
                    : ""
                } ${durationChunks.days} Days`;
              }

              return (
                <td>
                  <p className="m-0">{props.dataItem?.LeadDate}</p>
                  <p className="m-0">{remainingDuration}</p>
                </td>
              );
            }}
          />
          <Column field="LeadSource" title="Lead Source" width={200} />
          <Column field="LeadStage" title="Lead Stage" width={200} />
          <Column field="LeadOwnerName" title="Owner" width={200} />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"140px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteLeadDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const LeadID = useAppSelector((state) => state.lead.LeadID);
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const payload = {
        FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
        ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
        SectorID: dateFilter?.SectorID ? dateFilter?.SectorID : null,
      };
      const response = await dispatch(deleteLead(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getDashboardLeads(payload));
        dispatch(closeDialog());
        dispatch(clearLeadID());
      } else {
        dispatch(getDashboardLeads(payload));
        dispatch(closeDialog());
        dispatch(clearLeadID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteLead" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Lead?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Lead?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(LeadID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default DashboardLeads;
